import React, { Component } from "react";
import axios from "../../utils/axiosInstance";
import Swal from "sweetalert2";

class Expenses extends Component {
  state = {
    expenses: [],
    categories: [],
    formData: {
      id: null,
      expense_category_id: "",
      expense_description: "",
      expense_amount: "",
      expense_date: "",
      expense_status: "",
      expense_img: null,
    },
    imagePreview: "",
    deleteId: null,
    error: "",
    loading: false,
    submitting: false,
    modalMode: "add",
  };

  componentDidMount() {
    this.fetchCategories();
    this.fetchExpenses();

    const modalEl = document.getElementById("expenseModal");
    if (modalEl) {
      modalEl.addEventListener("hidden.bs.modal", () => {
        this.resetForm();
      });
    }
  }

  /** ✅ Fetch Expense Categories */
  fetchCategories = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get("/expenses/categories", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data?.data || [];
      this.setState({ categories: Array.isArray(data) ? data : [] });
    } catch (err) {
      console.error("Failed to load categories:", err);
    }
  };

  /** ✅ Fetch all Expenses */
  fetchExpenses = async () => {
    this.setState({ loading: true, error: "" });
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get("/expenses", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data?.data || [];
      this.setState({ expenses: Array.isArray(data) ? data : [] });
    } catch (err) {
      this.setState({
        error:
          err.response?.data?.message ||
          "Failed to load expenses. Please try again.",
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  /** ✅ Reset form when modal closes */
  resetForm = () => {
    this.setState({
      formData: {
        id: null,
        expense_category_id: "",
        expense_description: "",
        expense_amount: "",
        expense_date: "",
        expense_status: "",
        expense_img: null,
      },
      imagePreview: "",
      error: "",
      submitting: false,
      modalMode: "add",
    });
  };

  /** ✅ Input change handler */
  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      formData: { ...prev.formData, [name]: value },
    }));
  };

  /** ✅ File upload preview handler */
  handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      this.setState((prev) => ({
        formData: { ...prev.formData, expense_img: file },
        imagePreview: URL.createObjectURL(file),
      }));
    }
  };

  /** ✅ Open modal for add/edit */
  openModal = (mode = "add", expense = null) => {
    const modal = new window.bootstrap.Modal(document.getElementById("expenseModal"));
    const baseUrl = axios.defaults.baseURL.replace("/api", "");

    if (mode === "edit" && expense) {
      this.setState({
        modalMode: "edit",
        formData: {
          id: expense.id,
          expense_category_id: expense.expense_category_id || "",
          expense_description: expense.expense_description || "",
          expense_amount: expense.expense_amount || "",
          expense_date: expense.expense_date || "",
          expense_status: expense.expense_status?.toString() ?? "1",
          expense_img: null,
        },
        imagePreview: expense.expense_img
          ? `${baseUrl}/uploads/expenses/${expense.expense_img}`
          : "",
        error: "",
      });
    } else {
      this.resetForm();
    }

    modal.show();
  };

  /** ✅ Submit (add or update) */
  handleSubmit = async (e) => {
    e.preventDefault();
    const { formData, modalMode } = this.state;

    if (!formData.expense_category_id)
      return this.setState({ error: "Category is required." });
    if (!formData.expense_description)
      return this.setState({ error: "Expense description is required." });
    if (!formData.expense_amount)
      return this.setState({ error: "Expense amount is required." });
    if (!formData.expense_date)
      return this.setState({ error: "Expense date is required." });
    if (formData.expense_status === "")
      return this.setState({ error: "Status is required." });

    const data = new FormData();
    data.append("expense_category_id", formData.expense_category_id);
    data.append("expense_description", formData.expense_description);
    data.append("expense_amount", formData.expense_amount);
    data.append("expense_date", formData.expense_date);
    data.append("expense_status", formData.expense_status);
    if (formData.expense_img) data.append("expense_img", formData.expense_img);

    this.setState({ submitting: true, error: "" });

    try {
      const token = localStorage.getItem("token");
      if (modalMode === "add") {
        await axios.post("/expenses", data, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
      } else {
        await axios.post(`/expenses/${formData.id}?_method=PUT`, data, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
      }

      await this.fetchExpenses();
      const modal = window.bootstrap.Modal.getInstance(document.getElementById("expenseModal"));
      modal.hide();

      Swal.fire({
        icon: "success",
        title:
          modalMode === "add"
            ? "Expense added successfully!"
            : "Expense updated successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      console.error(err);
      this.setState({
        error:
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Error saving expense.",
      });
    } finally {
      this.setState({ submitting: false });
    }
  };

  /** ✅ Confirm Delete */
  confirmDelete = (id) => {
    this.setState({ deleteId: id });
    const modal = new window.bootstrap.Modal(document.getElementById("deleteModal"));
    modal.show();
  };

  /** ✅ Delete Expense */
  handleDelete = async () => {
    const { deleteId } = this.state;
    if (!deleteId) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/expenses/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await this.fetchExpenses();

      const modal = window.bootstrap.Modal.getInstance(document.getElementById("deleteModal"));
      modal.hide();

      Swal.fire({
        icon: "success",
        title: "Expense deleted successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch {
      Swal.fire({
        icon: "error",
        title: "Failed to delete expense.",
      });
    }
  };

  render() {
    const {
      expenses,
      categories,
      formData,
      imagePreview,
      error,
      loading,
      submitting,
      modalMode,
    } = this.state;

    const baseUrl = axios.defaults.baseURL.replace("/api", "");

    return (
      <div className="pc-container">
        <div className="pc-content">
          <div className="row">
            <div className="col-lg-12">
              <div className="card shadow-none">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <ul className="breadcrumb mb-0">
                    <li className="breadcrumb-item">Expenses</li>
                    <li className="breadcrumb-item active">List</li>
                  </ul>
                  <button
                    type="button"
                    className="btn btn-light-warning"
                    onClick={() => this.openModal("add")}
                  >
                    + New Expense
                  </button>
                </div>

                <div className="card-body">
                  {error && <div className="alert alert-danger">{error}</div>}
                  {loading ? (
                    <div className="text-center py-4">Loading...</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-bordered table-striped">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Category</th>
                            <th>Description</th>
                            <th>Amount</th>
                            <th>Date</th>
                            <th>Image</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {expenses.length ? (
                            expenses.map((exp, index) => (
                              <tr key={exp.id}>
                                <td>{index + 1}</td>
                                <td>{exp.expense_category_name || "N/A"}</td>
                                <td>{exp.expense_description}</td>
                                <td>{exp.expense_amount}</td>
                                <td>{exp.expense_date}</td>
                                <td>
                                  {exp.expense_img ? (
                                    <img
                                      src={`${baseUrl}/uploads/expenses/${exp.expense_img}`}
                                      alt="img"
                                      style={{
                                        width: "50px",
                                        height: "50px",
                                        borderRadius: "6px",
                                      }}
                                    />
                                  ) : (
                                    "No Image"
                                  )}
                                </td>
                                <td>
                                  {exp.expense_status === "1" ||
                                  exp.expense_status === 1 ? (
                                    <span className="badge bg-success">Active</span>
                                  ) : (
                                    <span className="badge bg-danger">Inactive</span>
                                  )}
                                </td>
                                <td>
                                  <button
                                    className="btn btn-sm btn-light-success me-2"
                                    onClick={() => this.openModal("edit", exp)}
                                  >
                                    <i className="ph ph-note-pencil"></i>
                                  </button>
                                  <button
                                    className="btn btn-sm btn-light-danger"
                                    onClick={() => this.confirmDelete(exp.id)}
                                  >
                                    <i className="ph ph-trash"></i>
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="8" className="text-center">
                                No expenses found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ✅ Add/Edit Modal */}
        <div className="modal fade" id="expenseModal" tabIndex={-1}>
          <div className="modal-dialog">
            <div className="modal-content">
              <form onSubmit={this.handleSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">
                    {modalMode === "add" ? "Add Expense" : "Edit Expense"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                  ></button>
                </div>

                <div className="modal-body">
                  {/* Category */}
                  <div className="mb-3">
                    <label className="form-label">Select Category</label>
                    <select
                      name="expense_category_id"
                      className="form-select"
                      value={formData.expense_category_id}
                      onChange={this.handleChange}
                      required
                    >
                      <option value="">-- Select Category --</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.category_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Expense Description */}
                  <div className="mb-3">
                    <label className="form-label">Expense Description</label>
                    <input
                      type="text"
                      name="expense_description"
                      className="form-control"
                      value={formData.expense_description}
                      onChange={this.handleChange}
                      required
                    />
                  </div>

                  {/* Amount */}
                  <div className="mb-3">
                    <label className="form-label">Amount</label>
                    <input
                      type="number"
                      name="expense_amount"
                      className="form-control"
                      value={formData.expense_amount}
                      onChange={this.handleChange}
                      required
                    />
                  </div>

                  {/* Date */}
                  <div className="mb-3">
                    <label className="form-label">Expense Date</label>
                    <input
                      type="date"
                      name="expense_date"
                      className="form-control"
                      value={formData.expense_date}
                      onChange={this.handleChange}
                      required
                    />
                  </div>

                  {/* Status */}
                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <select
                      name="expense_status"
                      className="form-select"
                      value={formData.expense_status}
                      onChange={this.handleChange}
                      required
                    >
                      <option value="">Select Status</option>
                      <option value="1">Active</option>
                      <option value="0">Inactive</option>
                    </select>
                  </div>

                  {/* Image Upload */}
                  <div className="mb-3">
                    <label className="form-label">Expense Image</label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={this.handleFileChange}
                    />
                    {imagePreview && (
                      <div className="mt-2 text-center">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          style={{ width: "100px", borderRadius: "8px" }}
                        />
                      </div>
                    )}
                  </div>

                  {error && <div className="alert alert-danger">{error}</div>}
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-light-danger"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-light-primary"
                    disabled={submitting}
                  >
                    {submitting
                      ? "Saving..."
                      : modalMode === "add"
                      ? "Add"
                      : "Update"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* ✅ Delete Modal */}
        <div className="modal fade" id="deleteModal" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-danger">
              <div className="modal-body text-center p-4">
                <i className="ph ph-warning text-danger f-40 mb-3"></i>
                <h5>Are you sure?</h5>
                <p className="text-muted mb-4">This action cannot be undone.</p>
                <div className="d-flex justify-content-center gap-3">
                  <button
                    type="button"
                    className="btn btn-light-secondary"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-light-danger"
                    onClick={this.handleDelete}
                  >
                    Yes, Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Expenses;
